import React, { useState } from 'react';
import {
  Window,
  WindowHeader,
  WindowContent,
  Button,
  Separator,
  GroupBox,
  Panel
} from 'react95';

/**
 * NavigatorPanel Component
 * 
 * Provides organized navigation sections for collection tools and client functions.
 * Implements state-based enabling/disabling of menu items based on current context.
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
const NavigatorPanel = ({ 
  currentClient = null, 
  onNavigate = () => {}, 
  navigationState = {
    activeSection: 'debt',
    expandedSections: ['debt', 'debtor'],
    availableFunctions: [],
    disabledFunctions: []
  }
}) => {
  const [expandedSections, setExpandedSections] = useState(
    navigationState.expandedSections || ['debt', 'debtor']
  );

  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => 
      prev.includes(sectionName)
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    );
  };

  // Check if a function should be enabled based on current state
  const isFunctionEnabled = (functionName) => {
    const { availableFunctions = [], disabledFunctions = [] } = navigationState;
    
    // If no client is selected, disable client-specific functions
    if (!currentClient && ['client-details', 'payment-arrangement', 'transaction-history'].includes(functionName)) {
      return false;
    }
    
    // Check explicit disabled functions
    if (disabledFunctions.includes(functionName)) {
      return false;
    }
    
    // If available functions are specified, only enable those
    if (availableFunctions.length > 0) {
      return availableFunctions.includes(functionName);
    }
    
    // Default to enabled
    return true;
  };

  // Handle navigation item click
  const handleNavigationClick = (functionName, sectionName) => {
    if (!isFunctionEnabled(functionName)) {
      return;
    }
    
    onNavigate({
      function: functionName,
      section: sectionName,
      client: currentClient
    });
  };

  // Navigation button component with state-based styling
  const NavigationButton = ({ functionName, sectionName, children, icon = '' }) => {
    const enabled = isFunctionEnabled(functionName);
    const isActive = navigationState.activeSection === sectionName;
    
    return (
      <Button
        onClick={() => handleNavigationClick(functionName, sectionName)}
        disabled={!enabled}
        style={{
          width: '100%',
          marginBottom: '2px',
          textAlign: 'left',
          fontSize: '11px',
          padding: '4px 8px',
          backgroundColor: isActive ? '#0066cc' : undefined,
          color: isActive ? 'white' : undefined,
          opacity: enabled ? 1 : 0.6
        }}
        size="sm"
      >
        {icon && <span style={{ marginRight: '6px' }}>{icon}</span>}
        {children}
      </Button>
    );
  };

  // Section header component
  const SectionHeader = ({ sectionName, title, icon = '' }) => {
    const isExpanded = expandedSections.includes(sectionName);
    
    return (
      <Button
        onClick={() => toggleSection(sectionName)}
        style={{
          width: '100%',
          marginBottom: '4px',
          textAlign: 'left',
          fontSize: '11px',
          fontWeight: 'bold',
          backgroundColor: '#e0e0e0',
          padding: '6px 8px'
        }}
        size="sm"
      >
        <span style={{ marginRight: '6px' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
        {icon && <span style={{ marginRight: '6px' }}>{icon}</span>}
        {title}
      </Button>
    );
  };

  return (
    <Window style={{ width: '200px', height: '600px' }}>
      <WindowHeader>
        <span>📋 Navigator</span>
      </WindowHeader>
      <WindowContent style={{ padding: '8px', overflow: 'auto' }}>
        
        {/* Current Client Info */}
        {currentClient && (
          <Panel variant="well" style={{ marginBottom: '10px', padding: '8px' }}>
            <div style={{ fontSize: '10px', color: '#666' }}>Current Client:</div>
            <div style={{ fontSize: '11px', fontWeight: 'bold' }}>
              {currentClient.clientName}
            </div>
            <div style={{ fontSize: '10px', color: '#666' }}>
              Code: {currentClient.debtCode}
            </div>
          </Panel>
        )}

        {/* Debt Section */}
        <div style={{ marginBottom: '8px' }}>
          <SectionHeader 
            sectionName="debt" 
            title="Debt" 
            icon="💰"
          />
          {expandedSections.includes('debt') && (
            <div style={{ paddingLeft: '8px' }}>
              <NavigationButton 
                functionName="debt-summary" 
                sectionName="debt"
                icon="📊"
              >
                Debt Summary
              </NavigationButton>
              <NavigationButton 
                functionName="payment-history" 
                sectionName="debt"
                icon="📋"
              >
                Payment History
              </NavigationButton>
              <NavigationButton 
                functionName="balance-inquiry" 
                sectionName="debt"
                icon="🔍"
              >
                Balance Inquiry
              </NavigationButton>
              <NavigationButton 
                functionName="payment-arrangement" 
                sectionName="debt"
                icon="📅"
              >
                Payment Arrangement
              </NavigationButton>
              <NavigationButton 
                functionName="settlement-options" 
                sectionName="debt"
                icon="🤝"
              >
                Settlement Options
              </NavigationButton>
            </div>
          )}
        </div>

        {/* Debtor Section */}
        <div style={{ marginBottom: '8px' }}>
          <SectionHeader 
            sectionName="debtor" 
            title="Debtor" 
            icon="👤"
          />
          {expandedSections.includes('debtor') && (
            <div style={{ paddingLeft: '8px' }}>
              <NavigationButton 
                functionName="client-details" 
                sectionName="debtor"
                icon="📝"
              >
                Client Details
              </NavigationButton>
              <NavigationButton 
                functionName="contact-information" 
                sectionName="debtor"
                icon="📞"
              >
                Contact Information
              </NavigationButton>
              <NavigationButton 
                functionName="client-search" 
                sectionName="debtor"
                icon="🔍"
              >
                Client Search
              </NavigationButton>
              <NavigationButton 
                functionName="client-notes" 
                sectionName="debtor"
                icon="📄"
              >
                Client Notes
              </NavigationButton>
              <NavigationButton 
                functionName="transaction-history" 
                sectionName="debtor"
                icon="📜"
              >
                Transaction History
              </NavigationButton>
            </div>
          )}
        </div>

        {/* Dialer Section */}
        <div style={{ marginBottom: '8px' }}>
          <SectionHeader 
            sectionName="dialer" 
            title="Dialer" 
            icon="☎️"
          />
          {expandedSections.includes('dialer') && (
            <div style={{ paddingLeft: '8px' }}>
              <NavigationButton 
                functionName="make-call" 
                sectionName="dialer"
                icon="📞"
              >
                Make Call
              </NavigationButton>
              <NavigationButton 
                functionName="call-history" 
                sectionName="dialer"
                icon="📋"
              >
                Call History
              </NavigationButton>
              <NavigationButton 
                functionName="call-recording" 
                sectionName="dialer"
                icon="🎙️"
              >
                Call Recording
              </NavigationButton>
              <NavigationButton 
                functionName="auto-dialer" 
                sectionName="dialer"
                icon="🔄"
              >
                Auto Dialer
              </NavigationButton>
              <NavigationButton 
                functionName="call-disposition" 
                sectionName="dialer"
                icon="✅"
              >
                Call Disposition
              </NavigationButton>
            </div>
          )}
        </div>

        {/* Queue Section */}
        <div style={{ marginBottom: '8px' }}>
          <SectionHeader 
            sectionName="queue" 
            title="Queue" 
            icon="📋"
          />
          {expandedSections.includes('queue') && (
            <div style={{ paddingLeft: '8px' }}>
              <NavigationButton 
                functionName="work-queue" 
                sectionName="queue"
                icon="📋"
              >
                Work Queue
              </NavigationButton>
              <NavigationButton 
                functionName="priority-queue" 
                sectionName="queue"
                icon="⚡"
              >
                Priority Queue
              </NavigationButton>
              <NavigationButton 
                functionName="callback-queue" 
                sectionName="queue"
                icon="📞"
              >
                Callback Queue
              </NavigationButton>
              <NavigationButton 
                functionName="follow-up-queue" 
                sectionName="queue"
                icon="🔄"
              >
                Follow-up Queue
              </NavigationButton>
              <NavigationButton 
                functionName="queue-management" 
                sectionName="queue"
                icon="⚙️"
              >
                Queue Management
              </NavigationButton>
            </div>
          )}
        </div>

        <Separator />
        
        {/* Quick Actions */}
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>
            Quick Actions
          </div>
          <NavigationButton 
            functionName="new-client" 
            sectionName="actions"
            icon="➕"
          >
            New Client
          </NavigationButton>
          <NavigationButton 
            functionName="reports" 
            sectionName="actions"
            icon="📊"
          >
            Reports
          </NavigationButton>
          <NavigationButton 
            functionName="settings" 
            sectionName="actions"
            icon="⚙️"
          >
            Settings
          </NavigationButton>
        </div>

        {/* Status Information */}
        <div style={{ 
          marginTop: '20px', 
          fontSize: '10px', 
          color: '#666',
          borderTop: '1px solid #ccc',
          paddingTop: '8px'
        }}>
          <div>Functions: {Object.keys(navigationState).length}</div>
          <div>Active: {navigationState.activeSection || 'None'}</div>
          {currentClient && (
            <div>Client: Selected</div>
          )}
        </div>
      </WindowContent>
    </Window>
  );
};

export default NavigatorPanel;